import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Order, ItemStatus } from "./order";
import { orderService } from "./orderService";
import { USE_STUB_DATA, STUB_ORDERS } from "../../config/stubData";
import { isSuccessResponse } from "../../services/enhancedApiClient";
import errorHandlingService from "../../services/errorHandlingService";
import { DEFAULT_PAGINATION } from "../../constants/pagination";

interface Pagination {
  page: number;
  limit: number;
  pages: number;
  total: number;
}

interface OrderState {
  orders: Order[];
  selectedOrder: Order | null;
  loading: boolean;
  error: string | null;
  pagination: Pagination;
  qrScanResult: any | null;
  lastViewedOrderId: string | null;
}

const initialState: OrderState = {
  orders: USE_STUB_DATA ? (STUB_ORDERS as unknown as Order[]) : [],
  selectedOrder: null,
  loading: false,
  error: null,
  pagination: {
    page: DEFAULT_PAGINATION.page,
    limit: DEFAULT_PAGINATION.limit,
    pages: 1,
    total: USE_STUB_DATA ? STUB_ORDERS.length : 0,
  },
  qrScanResult: null,
  lastViewedOrderId: null,
};

// Async thunks
export const fetchOrders = createAsyncThunk<
  { orders?: Order[]; pagination?: Pagination },
  {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    priority?: string;
  },
  { rejectValue: string }
>("orders/fetchOrders", async (params = {}, { rejectWithValue }) => {
  try {
    if (USE_STUB_DATA) {
      const {
        page = DEFAULT_PAGINATION.page,
        limit = DEFAULT_PAGINATION.limit,
        search = "",
        status = "",
        priority = "",
      } = params;

      // Apply filters to stub data
      let filteredOrders = STUB_ORDERS as unknown as Order[];

      if (search) {
        filteredOrders = filteredOrders.filter(
          (order: any) =>
            order.orderNumberNew
              ?.toLowerCase()
              .includes(search.toLowerCase()) ||
            order.customerName?.toLowerCase().includes(search.toLowerCase()) ||
            order.description?.toLowerCase().includes(search.toLowerCase())
        );
      }

      if (status) {
        filteredOrders = filteredOrders.filter(
          (order) => order.status === status
        );
      }

      if (priority) {
        filteredOrders = filteredOrders.filter(
          (order) => order.priority === priority
        );
      }

      // Apply pagination
      const total = filteredOrders.length;
      const pages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

      return {
        orders: paginatedOrders,
        pagination: {
          page,
          limit,
          pages,
          total,
        },
      };
    } else {
      // Filter out empty fields before making API call
      const filteredParams = { ...params };
      if (filteredParams.status === "" || filteredParams.status === undefined) {
        delete filteredParams.status;
      }
      if (filteredParams.search === "" || filteredParams.search === undefined) {
        delete filteredParams.search;
      }
      if (
        filteredParams.priority === "" ||
        filteredParams.priority === undefined
      ) {
        delete filteredParams.priority;
      }

      const response = await orderService.getOrders(filteredParams);

      if (isSuccessResponse(response)) {
        // Transform API data to match local Order interface if needed
        const transformedOrders = response.data.orders;
        return {
          orders: transformedOrders,
          pagination: response.data.pagination || {
            page: DEFAULT_PAGINATION.page,
            limit: DEFAULT_PAGINATION.limit,
            pages: 1,
            total: transformedOrders.length,
          },
        };
      } else {
        // Enhanced error handling for search requests
        let errorMessage = response.error?.message || "Failed to fetch orders";

        // Check for specific error types
        if (response.error?.code === "NO_ROLES") {
          errorMessage =
            "You don't have permission to view orders. Please contact your administrator.";
        } else if (response.error?.code === "WORKER_ACCESS_DENIED") {
          errorMessage = "Access denied. Please use the worker portal.";
        } else if (
          errorMessage.includes("403") ||
          errorMessage.includes("403.shtml")
        ) {
          errorMessage =
            "Access denied. Please check your permissions or contact your administrator.";
        }

        console.error("Order fetch error:", {
          error: response.error,
          params: filteredParams,
          timestamp: new Date().toISOString(),
        });

        return rejectWithValue(errorMessage);
      }
    }
  } catch (error: any) {
    await errorHandlingService.handleApiError(
      error,
      "Fetch Orders",
      "OrderSlice"
    );
    return rejectWithValue(error.message || "Failed to fetch orders");
  }
});

export const fetchOrderById = createAsyncThunk(
  "orders/fetchOrderById",
  async (orderId: string, { rejectWithValue }) => {
    try {
      if (USE_STUB_DATA) {
        const order = STUB_ORDERS.find((o) => o.id === orderId);
        if (!order) throw new Error("Order not found");
        return order as unknown as Order;
      } else {
        const response = await orderService.getOrder(orderId);

        if (isSuccessResponse(response)) {
          return response.data;
        } else {
          const errorMessage =
            response.error?.message || "Failed to fetch order";
          return rejectWithValue(errorMessage);
        }
      }
    } catch (error: any) {
      await errorHandlingService.handleApiError(
        error,
        "Fetch Order By ID",
        "OrderSlice"
      );
      return rejectWithValue(error.message || "Failed to fetch order");
    }
  }
);

export const addOrder = createAsyncThunk(
  "orders/addOrder",
  async (orderData: Partial<Order>, { rejectWithValue }) => {
    try {
      // Use the new createOrderWithItemTypes method for the dynamic structure
      const response = await orderService.createOrderWithItemTypes(orderData);
      console.log("======response:   ", response);
      console.log("+++++++response.data.order:   ", response.data.order);
      console.log(
        "********response.data.order.order:   ",
        response.data.order.order
      );

      if (isSuccessResponse(response)) {
        return response.data.order;
        // return response.data;
      } else {
        const errorMessage =
          response.error?.message || "Failed to create order";
        return rejectWithValue(errorMessage);
      }
    } catch (error: any) {
      await errorHandlingService.handleApiError(
        error,
        "Create Order",
        "OrderSlice"
      );
      return rejectWithValue(error.message || "Failed to create order");
    }
  }
);

export const updateOrderById = createAsyncThunk(
  "orders/updateOrder",
  async (
    { orderId, orderData }: { orderId: string; orderData: Partial<Order> },
    { rejectWithValue }
  ) => {
    try {
      if (USE_STUB_DATA) {
        const order = STUB_ORDERS.find((o) => o.id === orderId);
        if (!order) throw new Error("Order not found");
        const updatedOrder = {
          ...order,
          ...orderData,
          updatedAt: new Date(),
        } as unknown as Order;
        return updatedOrder;
      } else {
        // Use the new updateOrderWithItemTypes method for the dynamic structure
        const response = await orderService.updateOrderWithItemTypes(
          orderId,
          orderData
        );

        if (isSuccessResponse(response)) {
          return response.data.order;
        } else {
          const errorMessage =
            response.error?.message || "Failed to update order";
          return rejectWithValue(errorMessage);
        }
      }
    } catch (error: any) {
      await errorHandlingService.handleApiError(
        error,
        "Update Order",
        "OrderSlice"
      );
      return rejectWithValue(error.message || "Failed to update order");
    }
  }
);

export const updateOrderStatusById = createAsyncThunk(
  "orders/updateStatus",
  async (
    { orderId, status }: { orderId: string; status: ItemStatus },
    { rejectWithValue }
  ) => {
    try {
      if (USE_STUB_DATA) {
        const order = STUB_ORDERS.find((o) => o.id === orderId);
        if (!order) throw new Error("Order not found");
        const updatedOrder = {
          ...order,
          status,
          updatedAt: new Date(),
        } as unknown as Order;
        return updatedOrder;
      } else {
        const response = await orderService.updateOrderStatus(orderId, status);

        if (isSuccessResponse(response)) {
          return response.data;
        } else {
          const errorMessage =
            response.error?.message || "Failed to update order status";
          return rejectWithValue(errorMessage);
        }
      }
    } catch (error: any) {
      await errorHandlingService.handleApiError(
        error,
        "Update Order Status",
        "OrderSlice"
      );
      return rejectWithValue(error.message || "Failed to update order status");
    }
  }
);

export const deleteOrderById = createAsyncThunk(
  "orders/deleteOrder",
  async (orderId: string, { rejectWithValue }) => {
    try {
      if (USE_STUB_DATA) {
        const order = STUB_ORDERS.find((o) => o.id === orderId);
        if (!order) throw new Error("Order not found");
        return orderId;
      } else {
        const response = await orderService.deleteOrder(orderId);

        if (response.success) {
          return orderId; // Return the ID for removal from state
        } else {
          const errorMessage =
            response.error?.message || "Failed to delete order";
          return rejectWithValue(errorMessage);
        }
      }
    } catch (error: any) {
      await errorHandlingService.handleApiError(
        error,
        "Delete Order",
        "OrderSlice"
      );
      return rejectWithValue(error.message || "Failed to delete order");
    }
  }
);

// Create order with new item type structure
export const createOrderWithItemTypes = createAsyncThunk(
  "orders/createOrderWithItemTypes",
  async (orderData: any, { rejectWithValue }) => {
    try {
      const response = await orderService.createOrderWithItemTypes(orderData);

      if (isSuccessResponse(response)) {
        return response.data.order;
      } else {
        const errorMessage =
          response.error?.message || "Failed to create order";
        return rejectWithValue(errorMessage);
      }
    } catch (error: any) {
      await errorHandlingService.handleApiError(
        error,
        "Create Order",
        "OrderSlice"
      );
      return rejectWithValue(error.message || "Failed to create order");
    }
  }
);

// Update order with new item type structure
export const updateOrderWithItemTypes = createAsyncThunk(
  "orders/updateOrderWithItemTypes",
  async (
    { orderId, orderData }: { orderId: string; orderData: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await orderService.updateOrderWithItemTypes(
        orderId,
        orderData
      );

      if (isSuccessResponse(response)) {
        return response.data.order;
      } else {
        const errorMessage =
          response.error?.message || "Failed to update order";
        return rejectWithValue(errorMessage);
      }
    } catch (error: any) {
      await errorHandlingService.handleApiError(
        error,
        "Update Order",
        "OrderSlice"
      );
      return rejectWithValue(error.message || "Failed to update order");
    }
  }
);

const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setSelectedOrder: (state, action: PayloadAction<Order>) => {
      state.selectedOrder = action.payload;
    },
    clearSelectedOrder: (state) => {
      state.selectedOrder = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setPagination: (state, action: PayloadAction<Partial<Pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setQRScanResult: (state, action: PayloadAction<any | null>) => {
      state.qrScanResult = action.payload;
    },
    setLastViewedOrderId: (state, action: PayloadAction<string | null>) => {
      state.lastViewedOrderId = action.payload;
    },
    clearLastViewedOrderId: (state) => {
      state.lastViewedOrderId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch orders
      .addCase(fetchOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch single order
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action: any) => {
        state.loading = false;
        state.selectedOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Add order
      .addCase(addOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.unshift(action.payload);
      })
      .addCase(addOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update order
      .addCase(updateOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderById.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create order with new structure
      .addCase(createOrderWithItemTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOrderWithItemTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.orders.push(action.payload);
      })
      .addCase(createOrderWithItemTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update order with new structure
      .addCase(updateOrderWithItemTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderWithItemTypes.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.orders.findIndex(
          (order) => order.id === action.payload.id
        );
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
        if (state.selectedOrder?.id === action.payload.id) {
          state.selectedOrder = action.payload;
        }
      })
      .addCase(updateOrderWithItemTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update order status
      .addCase(updateOrderStatusById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderStatusById.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.orders.findIndex(
          (order) => order.id === action.payload.id
        );
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
        if (state.selectedOrder?.id === action.payload.id) {
          state.selectedOrder = action.payload;
        }
      })
      .addCase(updateOrderStatusById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete order
      .addCase(deleteOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = state.orders?.filter(
          (order) => order.id !== action.payload
        );
        if (state.selectedOrder?.id === action.payload) {
          state.selectedOrder = null;
        }
      })
      .addCase(deleteOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedOrder,
  clearSelectedOrder,
  clearError,
  setPagination,
  setQRScanResult,
  setLastViewedOrderId,
  clearLastViewedOrderId,
} = orderSlice.actions;

// Selectors
export const selectOrders = (state: any) => state.orders.orders;
export const selectOrderLoading = (state: any) => state.orders.loading;
export const selectOrderError = (state: any) => state.orders.error;
export const selectSelectedOrder = (state: any) => state.orders.selectedOrder;
export const selectOrderPagination = (state: any) => state.orders.pagination;
export const selectLastViewedOrderId = (state: any) => state.orders.lastViewedOrderId;

export default orderSlice.reducer;
